// Staff paperdoll prototype view - test layer compositing

import { css, html, nothing } from 'lit';
import { api } from '../../lib/api.js';
import { BaseComponent } from '../../lib/base-component.js';
import { deleteAlternate, getAllAlternates, saveAlternate, setUseAlternate } from '../../lib/paperdoll-alternates.js';
import { compositeLayersAsync, getLayerZOrder, SPECIES_PROFILES } from '../../rendering/paperdoll-compositor.js';

/**
 * @typedef {object} PaperdollLayer
 * @property {string} id
 * @property {string} slug
 * @property {string} category
 * @property {string} subcategory
 * @property {string} style
 * @property {string} colorIndex
 * @property {string} hand
 * @property {string} depth
 * @property {string} bodyVariant
 * @property {number} zOrder
 * @property {string} [imageUrl]
 * @property {string} [previewUrl]
 */

// Category display order and labels
const CATEGORY_SECTIONS = [
  { key: 'base', label: 'Base Body', categories: ['head', 'torso', 'legs'] },
  { key: 'species', label: 'Species Features', categories: ['ears', 'hair', 'hair-addon', 'horns', 'tusks', 'tail'] },
  {
    key: 'gear',
    label: 'Gear',
    categories: [
      'gear-hat',
      'gear-neck',
      'gear-handheld',
      'gear-arms',
      'gear-torso',
      'gear-waist',
      'gear-legs',
      'gear-footwear',
    ],
  },
];

/** @type {Record<string, string>} */
const CATEGORY_LABELS = {
  head: 'Head',
  torso: 'Torso',
  legs: 'Legs',
  ears: 'Ears',
  hair: 'Hair',
  'hair-addon': 'Hair Add-on',
  horns: 'Horns',
  tusks: 'Tusks',
  tail: 'Tail',
  'gear-hat': 'Hat',
  'gear-neck': 'Neck',
  'gear-handheld': 'Handheld',
  'gear-arms': 'Arms',
  'gear-torso': 'Torso Gear',
  'gear-waist': 'Waist',
  'gear-legs': 'Leg Gear',
  'gear-footwear': 'Footwear',
};

/** @type {Record<string, any>} */
const MULTI_SELECT_CONFIG = {
  'hair-addon': { type: 'sublayer-group' },
  'gear-neck': {
    type: 'mapped',
    map: {
      'cape-clean': 'back',
      'cape-ragged': 'back',
      pauldrons: 'shoulder',
      cowl: 'neck',
      'fur-mantel': 'neck',
      scarf: 'neck',
    },
    handSplit: ['shoulder'],
    labels: {
      back: 'Back',
      'shoulder:left': 'Left Shoulder',
      'shoulder:right': 'Right Shoulder',
      neck: 'Neck',
    },
  },
  'gear-handheld': { type: 'hand' },
  'gear-arms': {
    type: 'mapped',
    map: {
      'arm-armor': 'arms',
      'metal-bracers': 'arms',
      'fitted-gloves-long': 'hands',
      'fitted-gloves-medium': 'hands',
      'fitted-gloves-short': 'hands',
      'gloves-long': 'hands',
      bracelets: 'wrists',
    },
    handSplit: ['arms', 'hands', 'wrists'],
    labels: {
      'arms:left': 'Left Arm',
      'arms:right': 'Right Arm',
      'hands:left': 'Left Hand',
      'hands:right': 'Right Hand',
      'wrists:left': 'Left Wrist',
      'wrists:right': 'Right Wrist',
    },
  },
  'gear-waist': { type: 'subcategory' },
  'gear-legs': {
    type: 'mapped',
    map: {
      pants: 'legs',
      'skirt-side': 'legs',
      'skirt-split': 'legs',
      kneepad: 'kneepad',
    },
    labels: {
      legs: 'Legs',
      kneepad: 'Kneepad',
    },
  },
  'gear-footwear': { type: 'hand', filterBoth: true },
};

class StaffPaperdollView extends BaseComponent {
  static properties = {
    _layers: { type: Array, state: true },
    _loading: { type: Boolean, state: true },
    _error: { type: String, state: true },
    _selections: { type: Object, state: true },
    _skinTone: { type: String, state: true },
    _hairColor: { type: String, state: true },
    _species: { type: String, state: true },
    _contextMenuSlug: { type: String, state: true },
    _contextMenuPos: { type: Object, state: true },
    _uploadModalSlug: { type: String, state: true },
    _alternates: { type: Object, state: true },
  };

  static styles = css`
    :host {
      position: absolute;
      inset: 0;
      overflow: hidden;
    }

    .paperdoll-layout {
      display: flex;
      gap: 1.5rem;
      padding: 1rem 1.5rem;
      height: 100%;
    }

    .picker-column {
      flex: 1;
      min-width: 0;
      overflow-y: auto;
    }

    .preview-column {
      width: 340px;
      flex-shrink: 0;
    }

    .preview-viewport {
      width: 300px;
      height: 300px;
      border: 1px solid var(--bs-border-color, #dee2e6);
      border-radius: 0.375rem;
      background: #f0f0f0;
      overflow: hidden;
      cursor: grab;
      position: relative;
    }

    .preview-viewport:active {
      cursor: grabbing;
    }

    .preview-canvas {
      width: 300px;
      height: 300px;
      transform-origin: 0 0;
    }

    .preview-controls {
      position: absolute;
      bottom: 0.5rem;
      right: 0.5rem;
      display: flex;
      gap: 0.25rem;
    }

    .preview-controls button {
      font-size: 0.75rem;
      width: 28px;
      height: 28px;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 0;
      border: 1px solid rgba(0, 0, 0, 0.2);
      border-radius: 0.25rem;
      background: rgba(255, 255, 255, 0.85);
      cursor: pointer;
    }

    .preview-controls button:hover {
      background: rgba(255, 255, 255, 1);
    }

    .section-header {
      font-size: 0.875rem;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      color: var(--bs-secondary, #6c757d);
      margin-top: 1rem;
      margin-bottom: 0.5rem;
      padding-bottom: 0.25rem;
      border-bottom: 1px solid var(--bs-border-color, #dee2e6);
    }

    .category-picker {
      margin-bottom: 0.75rem;
    }

    .category-label {
      font-weight: 500;
      font-size: 0.8125rem;
      margin-bottom: 0.25rem;
    }

    .picker-row {
      display: flex;
      flex-wrap: wrap;
      gap: 0.25rem;
    }

    .layer-btn {
      width: 48px;
      height: 48px;
      padding: 2px;
      border: 2px solid transparent;
      border-radius: 0.25rem;
      background: var(--bs-body-bg, #fff);
      cursor: pointer;
      overflow: hidden;
    }

    .layer-btn:hover {
      border-color: var(--bs-primary, #0d6efd);
    }

    .layer-btn.selected {
      border-color: var(--bs-primary, #0d6efd);
      box-shadow: 0 0 0 2px rgba(13, 110, 253, 0.25);
    }

    .layer-btn img {
      width: 100%;
      height: 100%;
      object-fit: contain;
    }

    .none-btn {
      width: 48px;
      height: 48px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 0.625rem;
      color: var(--bs-secondary, #6c757d);
      border: 2px dashed var(--bs-border-color, #dee2e6);
      border-radius: 0.25rem;
      background: none;
      cursor: pointer;
    }

    .none-btn:hover {
      border-color: var(--bs-primary, #0d6efd);
    }

    .none-btn.selected {
      border-color: var(--bs-primary, #0d6efd);
      background: rgba(13, 110, 253, 0.05);
    }

    .skin-swatch-img {
      width: 36px;
      height: 36px;
      padding: 1px;
      border: 2px solid transparent;
      border-radius: 50%;
      background: none;
      cursor: pointer;
      overflow: hidden;
    }

    .skin-swatch-img img {
      width: 100%;
      height: 100%;
      object-fit: contain;
    }

    .skin-swatch-img:hover {
      border-color: var(--bs-primary, #0d6efd);
    }

    .skin-swatch-img.selected {
      border-color: var(--bs-primary, #0d6efd);
      box-shadow: 0 0 0 2px rgba(13, 110, 253, 0.25);
    }

    .subcategory-select {
      font-size: 0.8125rem;
      padding: 0.15rem 0.5rem;
      margin-bottom: 0.25rem;
    }

    .species-picker {
      display: flex;
      flex-wrap: wrap;
      gap: 0.25rem;
      margin-bottom: 0.5rem;
    }

    .species-btn {
      font-size: 0.7rem;
      padding: 0.2rem 0.5rem;
      border: 1px solid var(--bs-border-color, #dee2e6);
      border-radius: 0.25rem;
      background: none;
      cursor: pointer;
    }

    .species-btn:hover {
      border-color: var(--bs-primary, #0d6efd);
    }

    .species-btn.selected {
      border-color: var(--bs-primary, #0d6efd);
      background: rgba(13, 110, 253, 0.1);
      font-weight: 600;
    }

    /* Layer button wrapper for indicator dot */
    .layer-btn-wrap {
      position: relative;
      display: inline-block;
    }

    .alt-dot {
      position: absolute;
      top: -2px;
      right: -2px;
      width: 8px;
      height: 8px;
      border-radius: 50%;
      border: 1px solid #fff;
      pointer-events: none;
      z-index: 1;
    }

    .alt-dot.stored {
      background: var(--bs-primary, #0d6efd);
    }
    .alt-dot.active {
      background: var(--bs-success, #198754);
    }

    /* Context menu */
    .ctx-menu {
      position: fixed;
      z-index: 1050;
      min-width: 180px;
      background: var(--bs-body-bg, #fff);
      border: 1px solid var(--bs-border-color, #dee2e6);
      border-radius: 0.375rem;
      box-shadow: 0 0.25rem 0.75rem rgba(0, 0, 0, 0.15);
      padding: 0.25rem 0;
    }

    .ctx-menu-item {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      width: 100%;
      padding: 0.35rem 0.75rem;
      border: none;
      background: none;
      font-size: 0.8125rem;
      cursor: pointer;
      text-align: left;
      color: var(--bs-body-color, #212529);
    }

    .ctx-menu-item:hover {
      background: var(--bs-light, #f8f9fa);
    }

    .ctx-menu-item:disabled {
      opacity: 0.5;
      cursor: default;
    }

    .ctx-menu-check {
      width: 14px;
      text-align: center;
    }

    /* Upload modal */
    .upload-backdrop {
      position: fixed;
      inset: 0;
      z-index: 1060;
      background: rgba(0, 0, 0, 0.5);
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .upload-dialog {
      background: var(--bs-body-bg, #fff);
      border-radius: 0.5rem;
      padding: 1.5rem;
      width: 400px;
      max-width: 90vw;
    }

    .drop-zone {
      border: 2px dashed var(--bs-border-color, #dee2e6);
      border-radius: 0.375rem;
      padding: 2rem;
      text-align: center;
      cursor: pointer;
      transition: border-color 0.15s;
    }

    .drop-zone.dragover {
      border-color: var(--bs-primary, #0d6efd);
      background: rgba(13, 110, 253, 0.05);
    }

    .drop-zone img {
      max-width: 100%;
      max-height: 200px;
      margin-top: 0.5rem;
    }
  `;

  constructor() {
    super();
    /** @type {PaperdollLayer[]} */
    this._layers = [];
    this._loading = true;
    this._error = null;
    /** @type {{[key: string]: string|null}} key -> slug or null (compound keys like 'hair-addon:bangs' for multi-select) */
    this._selections = {};
    this._skinTone = '01';
    this._hairColor = '01';
    this._species = 'human';

    // Context menu state
    /** @type {string|null} */
    this._contextMenuSlug = null;
    /** @type {{x: number, y: number}|null} */
    this._contextMenuPos = null;

    // Upload modal state
    /** @type {string|null} */
    this._uploadModalSlug = null;
    /** @type {string|null} */
    this._uploadPreviewUrl = null;
    /** @type {Blob|null} */
    this._uploadBlob = null;

    // Alternates from IndexedDB: slug -> AlternateEntry
    /** @type {Map<string, import('../../lib/paperdoll-alternates.js').AlternateEntry>} */
    this._alternates = new Map();

    // Pan/zoom state
    this._zoom = 2.5;
    this._panX = -225;
    this._panY = -200;
    this._isPanning = false;
    this._panStartX = 0;
    this._panStartY = 0;
    this._panStartPanX = 0;
    this._panStartPanY = 0;

    this._onPreviewWheel = this._onPreviewWheel.bind(this);
    this._onPreviewPointerDown = this._onPreviewPointerDown.bind(this);
    this._onPreviewPointerMove = this._onPreviewPointerMove.bind(this);
    this._onPreviewPointerUp = this._onPreviewPointerUp.bind(this);
    this._onDismissContextMenu = this._onDismissContextMenu.bind(this);
    this._onKeyDown = this._onKeyDown.bind(this);
  }

  async connectedCallback() {
    await super.connectedCallback();
    this._alternates = await getAllAlternates();
    await this._fetchLayers();
    document.addEventListener('click', this._onDismissContextMenu);
    document.addEventListener('keydown', this._onKeyDown);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('click', this._onDismissContextMenu);
    document.removeEventListener('keydown', this._onKeyDown);
    // Revoke object URLs to prevent memory leaks
    for (const entry of this._alternates.values()) {
      if (entry.objectUrl) URL.revokeObjectURL(entry.objectUrl);
      if (entry.previewObjectUrl) URL.revokeObjectURL(entry.previewObjectUrl);
    }
  }

  async _fetchLayers() {
    this._loading = true;
    this._error = null;
    try {
      const data = await api.get('/api/paperdoll-layers');
      this._layers = data.layers || [];
      this._loading = false;
      this._restoreOrAutoSelect();
    } catch (err) {
      this._error = /** @type {Error} */ (err).message;
      this._loading = false;
    }
  }

  _restoreOrAutoSelect() {
    try {
      const saved = localStorage.getItem('paperdoll-selections');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed.skinTone) this._skinTone = parsed.skinTone;
        if (parsed.hairColor) this._hairColor = parsed.hairColor;
        if (parsed.species) this._species = parsed.species;
        if (parsed.selections) this._selections = parsed.selections;
        this._updatePreview();
        return;
      }
    } catch {
      /* ignore parse errors */
    }

    // Default: select head, torso, legs with skin tone 01
    for (const cat of ['head', 'torso', 'legs']) {
      const match = this._layers.find((l) => l.category === cat && l.colorIndex === this._skinTone);
      if (match) {
        this._selections = { ...this._selections, [cat]: match.slug };
      }
    }
    this._updatePreview();
  }

  _startOver() {
    localStorage.removeItem('paperdoll-selections');
    this._selections = {};
    this._skinTone = '01';
    this._hairColor = '01';
    this._species = 'human';
    this._restoreOrAutoSelect();
  }

  _saveSelections() {
    localStorage.setItem(
      'paperdoll-selections',
      JSON.stringify({
        skinTone: this._skinTone,
        hairColor: this._hairColor,
        species: this._species,
        selections: this._selections,
      }),
    );
  }

  /**
   * Get layers for a category, optionally filtered
   * @param {string} category
   * @returns {PaperdollLayer[]}
   */
  _getLayersForCategory(category) {
    return this._layers.filter((l) => l.category === category);
  }

  /**
   * Get unique subcategories for a category
   * @param {string} category
   * @returns {string[]}
   */
  _getSubcategories(category) {
    const subs = new Set(
      this._getLayersForCategory(category)
        .map((l) => l.subcategory)
        .filter(Boolean),
    );
    return [...subs].sort();
  }

  /**
   * Get unique styles for a category
   * @param {string} category
   * @returns {string[]}
   */
  _getStyles(category) {
    const styles = new Set(
      this._getLayersForCategory(category)
        .map((l) => l.style)
        .filter(Boolean),
    );
    return [...styles].sort();
  }

  /**
   * Handle skin tone change - update all body categories
   * @param {string} tone
   */
  _setSkinTone(tone) {
    this._skinTone = tone;
    const updated = { ...this._selections };

    // Update body categories to match the new skin tone
    for (const cat of ['head', 'torso', 'legs', 'ears', 'horns', 'tail']) {
      const currentSlug = updated[cat];
      if (!currentSlug) continue;
      const current = this._layers.find((l) => l.slug === currentSlug);
      if (!current) continue;
      // Find the same style but with new skin tone
      const match = this._layers.find(
        (l) =>
          l.category === cat &&
          l.style === current.style &&
          l.subcategory === current.subcategory &&
          l.colorIndex === tone,
      );
      if (match) {
        updated[cat] = match.slug;
      }
    }

    this._selections = updated;
    this._saveSelections();
    this._updatePreview();
  }

  /**
   * @param {string} species
   */
  _setSpecies(species) {
    this._species = species;
    this._saveSelections();
    this._updatePreview();
  }

  /** @param {string} color */
  _setHairColor(color) {
    this._hairColor = color;
    const updated = { ...this._selections };

    // Update hair selection to match new color
    const hairSlug = updated.hair;
    if (hairSlug) {
      const current = this._layers.find((l) => l.slug === hairSlug);
      if (current) {
        const match = this._layers.find(
          (l) =>
            l.category === 'hair' &&
            l.style === current.style &&
            l.subcategory === current.subcategory &&
            l.colorIndex === color,
        );
        if (match) updated.hair = match.slug;
      }
    }

    // Update all hair-addon selections to match new color
    for (const key of Object.keys(updated)) {
      if (!key.startsWith('hair-addon:')) continue;
      const slug = updated[key];
      if (!slug) continue;
      const current = this._layers.find((l) => l.slug === slug);
      if (!current) continue;
      const match = this._layers.find(
        (l) =>
          l.category === 'hair-addon' &&
          l.style === current.style &&
          l.subcategory === current.subcategory &&
          l.colorIndex === color,
      );
      if (match) updated[key] = match.slug;
    }

    this._selections = updated;
    this._saveSelections();
    this._updatePreview();
  }

  /**
   * Select a layer by slug for a key (plain category or compound like 'hair-addon:bangs')
   * @param {string} key
   * @param {string|null} slug
   */
  _selectLayer(key, slug) {
    this._selections = { ...this._selections, [key]: slug };

    // When torso changes, update gear-torso to match the new body variant
    if (key === 'torso' && slug) {
      const newStyle = this._layers.find((l) => l.slug === slug)?.style;
      const gearSlug = this._selections['gear-torso'];
      if (gearSlug && newStyle) {
        const gear = this._layers.find((l) => l.slug === gearSlug);
        if (gear && gear.bodyVariant !== newStyle) {
          const match = this._layers.find(
            (l) =>
              l.category === 'gear-torso' &&
              l.subcategory === gear.subcategory &&
              l.colorIndex === gear.colorIndex &&
              l.bodyVariant === newStyle,
          );
          this._selections['gear-torso'] = match?.slug || null;
        }
      }
    }

    this._saveSelections();
    this._updatePreview();
  }

  /** @param {WheelEvent} e */
  _onPreviewWheel(e) {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    const newZoom = Math.min(8, Math.max(0.25, this._zoom * delta));

    // Zoom toward cursor position
    const viewport = this.renderRoot?.querySelector('.preview-viewport');
    if (viewport) {
      const rect = viewport.getBoundingClientRect();
      const cx = e.clientX - rect.left;
      const cy = e.clientY - rect.top;
      this._panX = cx - (cx - this._panX) * (newZoom / this._zoom);
      this._panY = cy - (cy - this._panY) * (newZoom / this._zoom);
    }

    this._zoom = newZoom;
    this._applyPreviewTransform();
  }

  /** @param {PointerEvent} e */
  _onPreviewPointerDown(e) {
    // Don't start panning if clicking a control button
    if (/** @type {HTMLElement} */ (e.target).closest('.preview-controls')) return;
    this._isPanning = true;
    this._panStartX = e.clientX;
    this._panStartY = e.clientY;
    this._panStartPanX = this._panX;
    this._panStartPanY = this._panY;
    /** @type {HTMLElement} */ (e.currentTarget).setPointerCapture(e.pointerId);
  }

  /** @param {PointerEvent} e */
  _onPreviewPointerMove(e) {
    if (!this._isPanning) return;
    this._panX = this._panStartPanX + (e.clientX - this._panStartX);
    this._panY = this._panStartPanY + (e.clientY - this._panStartY);
    this._applyPreviewTransform();
  }

  /** @param {PointerEvent} _e */
  _onPreviewPointerUp(_e) {
    this._isPanning = false;
  }

  /** @param {number} factor */
  _zoomPreview(factor) {
    const newZoom = Math.min(8, Math.max(0.25, this._zoom * factor));
    // Zoom toward center of viewport (150, 150 for 300x300)
    const cx = 150;
    const cy = 150;
    this._panX = cx - (cx - this._panX) * (newZoom / this._zoom);
    this._panY = cy - (cy - this._panY) * (newZoom / this._zoom);
    this._zoom = newZoom;
    this._applyPreviewTransform();
  }

  _resetPreviewTransform() {
    this._zoom = 2.5;
    this._panX = -225;
    this._panY = -200;
    this._applyPreviewTransform();
  }

  _applyPreviewTransform() {
    const canvas = /** @type {HTMLElement|null} */ (this.renderRoot?.querySelector('#preview-canvas'));
    if (!canvas) return;
    canvas.style.transform = `translate(${this._panX}px, ${this._panY}px) scale(${this._zoom})`;
  }

  // --- Context menu ---

  /** @param {MouseEvent} e */
  _onDismissContextMenu(e) {
    if (this._contextMenuSlug && !(/** @type {HTMLElement} */ (e.target).closest('.ctx-menu'))) {
      this._contextMenuSlug = null;
    }
  }

  /** @param {KeyboardEvent} e */
  _onKeyDown(e) {
    if (e.key === 'Escape') {
      if (this._uploadModalSlug) {
        this._closeUploadModal();
        return;
      }
      if (this._contextMenuSlug) {
        this._contextMenuSlug = null;
      }
    }
  }

  /**
   * @param {MouseEvent} e
   * @param {string} slug
   */
  _onLayerContextMenu(e, slug) {
    e.preventDefault();
    // Position relative to the shadow host so it works with fixed positioning
    this._contextMenuSlug = slug;
    this._contextMenuPos = { x: e.clientX, y: e.clientY };
  }

  /** Download the full-size asset for a layer slug
   * @param {string} slug */
  async _downloadAsset(slug) {
    this._contextMenuSlug = null;
    const layer = this._layers.find((l) => l.slug === slug);
    if (!layer?.imageUrl) return;
    const response = await fetch(layer.imageUrl);
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${slug}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  /** Open the upload modal for a slug
   * @param {string} slug */
  _openUploadModal(slug) {
    this._contextMenuSlug = null;
    this._uploadModalSlug = slug;
    this._uploadPreviewUrl = null;
    this._uploadBlob = null;
  }

  _closeUploadModal() {
    if (this._uploadPreviewUrl) URL.revokeObjectURL(this._uploadPreviewUrl);
    this._uploadModalSlug = null;
    this._uploadPreviewUrl = null;
    this._uploadBlob = null;
  }

  /** Toggle useAlternate for a slug
   * @param {string} slug */
  async _toggleUseAlternate(slug) {
    this._contextMenuSlug = null;
    const entry = this._alternates.get(slug);
    if (!entry) return;
    const newVal = !entry.useAlternate;
    await setUseAlternate(slug, newVal);
    entry.useAlternate = newVal;
    this._alternates = new Map(this._alternates);
    this._updatePreview();
  }

  /** @param {string} slug */
  async _removeAlternate(slug) {
    this._contextMenuSlug = null;
    const entry = this._alternates.get(slug);
    if (!entry) return;
    if (entry.objectUrl) URL.revokeObjectURL(entry.objectUrl);
    if (entry.previewObjectUrl) URL.revokeObjectURL(entry.previewObjectUrl);
    await deleteAlternate(slug);
    this._alternates.delete(slug);
    this._alternates = new Map(this._alternates);
    this._updatePreview();
  }

  // --- Upload modal handlers ---

  /** @param {DragEvent} e */
  _onDropZoneDragOver(e) {
    e.preventDefault();
    /** @type {HTMLElement} */ (e.currentTarget).classList.add('dragover');
  }

  /** @param {DragEvent} e */
  _onDropZoneDragLeave(e) {
    /** @type {HTMLElement} */ (e.currentTarget).classList.remove('dragover');
  }

  /** @param {DragEvent} e */
  _onDropZoneDrop(e) {
    e.preventDefault();
    /** @type {HTMLElement} */ (e.currentTarget).classList.remove('dragover');
    const file = e.dataTransfer?.files[0];
    if (file?.type.startsWith('image/')) {
      this._setUploadFile(file);
    }
  }

  /** @param {Event} e */
  _onFileInputChange(e) {
    const file = /** @type {HTMLInputElement} */ (e.target).files?.[0];
    if (file?.type.startsWith('image/')) {
      this._setUploadFile(file);
    }
  }

  /** @param {File} file */
  _setUploadFile(file) {
    if (this._uploadPreviewUrl) URL.revokeObjectURL(this._uploadPreviewUrl);
    this._uploadBlob = file;
    this._uploadPreviewUrl = URL.createObjectURL(file);
    this.requestUpdate();
  }

  async _saveUploadedAlternate() {
    if (!this._uploadModalSlug || !this._uploadBlob) return;
    const slug = this._uploadModalSlug;
    // Revoke old object URLs if they existed
    const existing = this._alternates.get(slug);
    if (existing?.objectUrl) URL.revokeObjectURL(existing.objectUrl);
    if (existing?.previewObjectUrl) URL.revokeObjectURL(existing.previewObjectUrl);

    const entry = await saveAlternate(slug, this._uploadBlob);
    this._alternates.set(slug, entry);
    this._alternates = new Map(this._alternates);
    this._closeUploadModal();
    this._updatePreview();
  }

  async _updatePreview() {
    await this.updateComplete;
    const canvas = this.renderRoot?.querySelector('#preview-canvas');
    if (!canvas) return;

    // Gather all selected layers, swapping in alternate URLs when active
    const selectedLayers = [];
    for (const [key, slug] of Object.entries(this._selections)) {
      if (!slug) continue;
      const layer = this._layers.find((l) => l.slug === slug);
      if (!layer?.imageUrl) continue;

      const alt = this._alternates.get(slug);
      const imageUrl = alt?.useAlternate && alt.objectUrl ? alt.objectUrl : layer.imageUrl;
      selectedLayers.push({ imageUrl, zOrder: getLayerZOrder(layer) });

      // For gear-handheld, auto-include paired front/back layer
      if (key.startsWith('gear-handheld:') && (layer.depth === 'front' || layer.depth === 'back')) {
        const pairedDepth = layer.depth === 'front' ? 'back' : 'front';
        const paired = this._layers.find(
          (l) =>
            l.category === 'gear-handheld' &&
            l.hand === layer.hand &&
            l.subcategory === layer.subcategory &&
            l.style === layer.style &&
            l.colorIndex === layer.colorIndex &&
            l.depth === pairedDepth,
        );
        if (paired?.imageUrl) {
          const pAlt = this._alternates.get(paired.slug);
          const pUrl = pAlt?.useAlternate && pAlt.objectUrl ? pAlt.objectUrl : paired.imageUrl;
          selectedLayers.push({ imageUrl: pUrl, zOrder: getLayerZOrder(paired) });
        }
      }
    }

    const profile =
      this._species !== 'human'
        ? /** @type {Record<string, {scale: number, translateY: number}>} */ (SPECIES_PROFILES)[this._species]
        : undefined;
    await compositeLayersAsync(/** @type {HTMLCanvasElement} */ (canvas), selectedLayers, profile);
    this._applyPreviewTransform();
  }

  /**
   * Render a single layer button with alternate indicator dot and context menu
   * @param {PaperdollLayer} layer
   * @param {boolean} isSelected
   * @param {(slug: string) => void} onSelect
   * @returns {import('lit').TemplateResult}
   */
  _renderLayerButton(layer, isSelected, onSelect) {
    const alt = this._alternates.get(layer.slug);
    const dotClass = alt ? (alt.useAlternate ? 'alt-dot active' : 'alt-dot stored') : '';
    const thumbUrl = alt?.useAlternate && alt.previewObjectUrl ? alt.previewObjectUrl : layer.previewUrl;
    return html`
      <div class="layer-btn-wrap">
        ${dotClass ? html`<span class="${dotClass}"></span>` : nothing}
        <button
          class="layer-btn ${isSelected ? 'selected' : ''}"
          title="${layer.slug}"
          @click=${() => onSelect(layer.slug)}
          @contextmenu=${(/** @type {MouseEvent} */ e) => this._onLayerContextMenu(e, layer.slug)}
        >
          ${
            thumbUrl
              ? html`<img src="${thumbUrl}" alt="${layer.slug}" loading="lazy" />`
              : html`<span style="font-size:0.5rem">${layer.subcategory || layer.style}</span>`
          }
        </button>
      </div>
    `;
  }

  /**
   * Render a category picker
   * @param {string} category
   * @returns {import('lit').TemplateResult}
   */
  _renderCategoryPicker(category) {
    const allLayers = this._getLayersForCategory(category);
    if (allLayers.length === 0) return html``;

    const subcategories = this._getSubcategories(category);
    const selectedSlug = this._selections[category] || null;
    const skinToneFiltered = ['head', 'torso', 'legs', 'ears', 'horns', 'tail'].includes(category);
    const isRequiredBody = ['head', 'torso', 'legs'].includes(category);
    const isHair = category === 'hair';

    // For skin-tone categories, filter to current skin tone; for hair, filter to current hair color
    let displayLayers = allLayers;
    if (skinToneFiltered) {
      displayLayers = allLayers.filter((l) => l.colorIndex === this._skinTone);
    } else if (isHair) {
      displayLayers = allLayers.filter((l) => l.colorIndex === this._hairColor);
    }

    // For gear-torso, filter to match the selected torso's body variant
    if (category === 'gear-torso') {
      const torsoSlug = this._selections.torso;
      const bodyVariant = torsoSlug ? this._layers.find((l) => l.slug === torsoSlug)?.style || 'a1' : 'a1';
      displayLayers = displayLayers.filter((l) => l.bodyVariant === bodyVariant);
    }

    // If there are subcategories, group by subcategory
    if (subcategories.length > 1) {
      return html`
        <div class="category-picker">
          <div class="category-label">${CATEGORY_LABELS[category] || category}</div>
          <div class="picker-row">
            <button
              class="none-btn ${!selectedSlug ? 'selected' : ''}"
              @click=${() => this._selectLayer(category, null)}
            >
              None
            </button>
            ${displayLayers.map((layer) =>
              this._renderLayerButton(layer, selectedSlug === layer.slug, (slug) => this._selectLayer(category, slug)),
            )}
          </div>
        </div>
      `;
    }

    // Simple picker (no subcategories or only one)
    return html`
      <div class="category-picker">
        <div class="category-label">${CATEGORY_LABELS[category] || category}</div>
        <div class="picker-row">
          ${
            !isRequiredBody
              ? html`
                <button
                  class="none-btn ${!selectedSlug ? 'selected' : ''}"
                  @click=${() => this._selectLayer(category, null)}
                >
                  None
                </button>
              `
              : ''
          }
          ${displayLayers.map((layer) =>
            this._renderLayerButton(layer, selectedSlug === layer.slug, (slug) => this._selectLayer(category, slug)),
          )}
        </div>
      </div>
    `;
  }

  /**
   * Render skin tone strip using actual head previews
   */
  _renderSkinToneStrip() {
    // Get the current head style to show matching skin tones
    const currentHead = this._selections.head;
    const currentStyle = currentHead ? this._layers.find((l) => l.slug === currentHead)?.style || 'a1' : 'a1';

    // Get all head layers for this style, one per color index
    const headLayers = this._layers
      .filter((l) => l.category === 'head' && l.style === currentStyle)
      .sort((a, b) => a.colorIndex.localeCompare(b.colorIndex));

    return html`
      <div class="category-picker">
        <div class="category-label">Skin Tone</div>
        <div class="picker-row">
          ${headLayers.map(
            (layer) => html`
              <button
                class="skin-swatch-img ${this._skinTone === layer.colorIndex ? 'selected' : ''}"
                title="Tone ${layer.colorIndex}"
                @click=${() => this._setSkinTone(layer.colorIndex)}
              >
                ${
                  layer.previewUrl
                    ? html`<img src="${layer.previewUrl}" alt="Tone ${layer.colorIndex}" />`
                    : html`<span>${layer.colorIndex}</span>`
                }
              </button>
            `,
          )}
        </div>
      </div>
    `;
  }

  /**
   * Render hair color strip using current hair style previews
   */
  _renderHairColorStrip() {
    // Use the current hair's subcategory for color previews
    const currentHairSlug = this._selections.hair;
    let currentSub;
    if (currentHairSlug) {
      currentSub = this._layers.find((l) => l.slug === currentHairSlug)?.subcategory;
    }
    if (!currentSub) {
      currentSub = this._layers.find((l) => l.category === 'hair')?.subcategory;
    }
    if (!currentSub) return html``;

    const colorLayers = this._layers
      .filter((l) => l.category === 'hair' && l.subcategory === currentSub)
      .sort((a, b) => a.colorIndex.localeCompare(b.colorIndex));

    if (colorLayers.length === 0) return html``;

    return html`
      <div class="category-picker">
        <div class="category-label">Hair Color</div>
        <div class="picker-row">
          ${colorLayers.map(
            (layer) => html`
              <button
                class="skin-swatch-img ${this._hairColor === layer.colorIndex ? 'selected' : ''}"
                title="Color ${layer.colorIndex}"
                @click=${() => this._setHairColor(layer.colorIndex)}
              >
                ${
                  layer.previewUrl
                    ? html`<img src="${layer.previewUrl}" alt="Color ${layer.colorIndex}" />`
                    : html`<span>${layer.colorIndex}</span>`
                }
              </button>
            `,
          )}
        </div>
      </div>
    `;
  }

  /**
   * Render a multi-select category with independent sub-pickers
   * @param {string} category
   * @returns {import('lit').TemplateResult}
   */
  _renderMultiSelectCategory(category) {
    const config = MULTI_SELECT_CONFIG[category];
    const allLayers = this._getLayersForCategory(category);
    if (allLayers.length === 0) return html``;

    /** @type {Array<{key: string, label: string, layers: PaperdollLayer[]}>} */
    let groups = [];

    switch (config.type) {
      case 'sublayer-group': {
        const groupMap = new Map();
        for (const layer of allLayers) {
          const prefix = layer.subcategory?.split('-')[0] || 'other';
          if (!groupMap.has(prefix)) groupMap.set(prefix, []);
          groupMap.get(prefix).push(layer);
        }
        groups = [...groupMap.entries()]
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([prefix, layers]) => ({
            key: `${category}:${prefix}`,
            label: prefix.charAt(0).toUpperCase() + prefix.slice(1),
            layers,
          }));
        break;
      }
      case 'hand': {
        const groupMap = new Map();
        for (const layer of allLayers) {
          const hand = layer.hand || 'both';
          if (config.filterBoth && hand === 'both') continue;
          if (!groupMap.has(hand)) groupMap.set(hand, []);
          groupMap.get(hand).push(layer);
        }
        const handOrder = ['left', 'right', 'both'];
        groups = handOrder
          .filter((h) => groupMap.has(h))
          .map((hand) => ({
            key: `${category}:${hand}`,
            label: hand === 'both' ? 'Both' : hand.charAt(0).toUpperCase() + hand.slice(1),
            layers: groupMap.get(hand),
          }));
        break;
      }
      case 'subcategory': {
        const groupMap = new Map();
        for (const layer of allLayers) {
          const sub = layer.subcategory || 'other';
          if (!groupMap.has(sub)) groupMap.set(sub, []);
          groupMap.get(sub).push(layer);
        }
        groups = [...groupMap.entries()]
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([sub, layers]) => ({
            key: `${category}:${sub}`,
            label: sub
              .split('-')
              .map(/** @param {string} w */ (w) => w.charAt(0).toUpperCase() + w.slice(1))
              .join(' '),
            layers,
          }));
        break;
      }
      case 'mapped': {
        const groupMap = new Map();
        for (const layer of allLayers) {
          const groupName = config.map[layer.subcategory];
          if (!groupName) continue;

          if (config.handSplit?.includes(groupName)) {
            if (!layer.hand) continue; // drop "both" assets
            const handKey = `${groupName}:${layer.hand}`;
            if (!groupMap.has(handKey)) groupMap.set(handKey, []);
            groupMap.get(handKey).push(layer);
          } else {
            if (!groupMap.has(groupName)) groupMap.set(groupName, []);
            groupMap.get(groupName).push(layer);
          }
        }

        // Preserve declaration order from map values, expand hand splits
        const seen = new Set();
        const orderedKeys = [];
        for (const groupName of Object.values(config.map)) {
          if (seen.has(groupName)) continue;
          seen.add(groupName);
          if (config.handSplit?.includes(groupName)) {
            if (groupMap.has(`${groupName}:left`)) orderedKeys.push(`${groupName}:left`);
            if (groupMap.has(`${groupName}:right`)) orderedKeys.push(`${groupName}:right`);
          } else {
            if (groupMap.has(groupName)) orderedKeys.push(groupName);
          }
        }

        groups = orderedKeys.map((groupKey) => ({
          key: `${category}:${groupKey}`,
          label: config.labels?.[groupKey] || groupKey,
          layers: groupMap.get(groupKey),
        }));
        break;
      }
    }

    // For hair-addon, filter layers to current hair color
    if (category === 'hair-addon') {
      for (const group of groups) {
        group.layers = group.layers.filter((l) => l.colorIndex === this._hairColor);
      }
    }

    // For gear-handheld, show one entry per weapon (subcategory+color).
    // Front+back pairs and combined are all just "one weapon" to the user.
    if (category === 'gear-handheld') {
      for (const group of groups) {
        const byWeapon = new Map();
        for (const layer of group.layers) {
          if (layer.depth === 'back') continue;
          const weaponKey = `${layer.subcategory}:${layer.colorIndex}`;
          const existing = byWeapon.get(weaponKey);

          if (!existing) {
            byWeapon.set(weaponKey, { ...layer });
          } else if (layer.depth === 'front' && existing.depth !== 'front') {
            // Use front for selection, keep combined's preview
            byWeapon.set(weaponKey, { ...layer, previewUrl: existing.previewUrl });
          } else if (layer.depth === 'combined' && existing.depth === 'front') {
            // Use combined's preview on the front entry
            existing.previewUrl = layer.previewUrl;
          }
        }
        group.layers = [...byWeapon.values()];
      }
    }

    return html`
      <div class="category-picker">
        <div class="category-label">${CATEGORY_LABELS[category] || category}</div>
        ${groups.map(
          (group) => html`
            <div style="margin-bottom: 0.35rem;">
              <div style="font-size: 0.7rem; color: var(--bs-secondary); margin-bottom: 0.15rem;">${group.label}</div>
              <div class="picker-row">
                <button
                  class="none-btn ${!this._selections[group.key] ? 'selected' : ''}"
                  @click=${() => this._selectLayer(group.key, null)}
                >
                  None
                </button>
                ${group.layers.map((layer) =>
                  this._renderLayerButton(layer, this._selections[group.key] === layer.slug, (slug) =>
                    this._selectLayer(group.key, slug),
                  ),
                )}
              </div>
            </div>
          `,
        )}
      </div>
    `;
  }

  render() {
    if (this._loading) {
      return html`
        <div class="text-center py-4">
          <div class="spinner-border spinner-border-sm" role="status"></div>
          <span class="ms-2">Loading paperdoll layers...</span>
        </div>
      `;
    }

    if (this._error) {
      return html` <div class="alert alert-danger">${this._error}</div> `;
    }

    if (this._layers.length === 0) {
      return html`
        <div class="alert alert-info">No paperdoll layers found. Run <code>make seed</code> after syncing assets.</div>
      `;
    }

    return html`
      <div class="paperdoll-layout">
        <div class="picker-column">
          <h5 class="mb-2">Paper Doll Prototype</h5>
          ${CATEGORY_SECTIONS.map(
            (section) => html`
              <div class="section-header">${section.label}</div>
              ${section.key === 'base' ? this._renderSkinToneStrip() : ''}
              ${section.key === 'species' ? this._renderHairColorStrip() : ''}
              ${section.categories.map((cat) =>
                MULTI_SELECT_CONFIG[cat] ? this._renderMultiSelectCategory(cat) : this._renderCategoryPicker(cat),
              )}
            `,
          )}
        </div>
        <div class="preview-column">
          <div class="d-flex align-items-center justify-content-between mb-2">
            <h6 class="mb-0">Preview</h6>
            <button class="btn btn-sm btn-outline-danger" @click=${this._startOver}>
              <i class="bi bi-arrow-counterclockwise"></i> Start Over
            </button>
          </div>
          <div class="species-picker">
            ${Object.keys(SPECIES_PROFILES).map(
              (species) => html`
                <button
                  class="species-btn ${this._species === species ? 'selected' : ''}"
                  @click=${() => this._setSpecies(species)}
                >
                  ${species.charAt(0).toUpperCase() + species.slice(1)}
                </button>
              `,
            )}
          </div>
          <div
            class="preview-viewport"
            @wheel=${this._onPreviewWheel}
            @pointerdown=${this._onPreviewPointerDown}
            @pointermove=${this._onPreviewPointerMove}
            @pointerup=${this._onPreviewPointerUp}
          >
            <canvas id="preview-canvas" class="preview-canvas" width="1200" height="1200"></canvas>
            <div class="preview-controls">
              <button @click=${() => this._zoomPreview(1.3)} title="Zoom in"><i class="bi bi-zoom-in"></i></button>
              <button @click=${() => this._zoomPreview(1 / 1.3)} title="Zoom out">
                <i class="bi bi-zoom-out"></i>
              </button>
              <button @click=${() => this._resetPreviewTransform()} title="Reset zoom">
                <i class="bi bi-fullscreen"></i>
              </button>
            </div>
          </div>
          <div class="text-muted" style="font-size: 0.7rem; margin-top: 0.25rem">
            ${Object.values(this._selections).filter(Boolean).length} layers selected
          </div>
        </div>
      </div>
      ${this._renderContextMenu()} ${this._renderUploadModal()}
    `;
  }

  _renderContextMenu() {
    if (!this._contextMenuSlug || !this._contextMenuPos) return nothing;
    const slug = this._contextMenuSlug;
    const alt = this._alternates.get(slug);
    const hasAlt = !!alt;
    const isActive = alt?.useAlternate ?? false;
    return html`
      <div class="ctx-menu" style="left:${this._contextMenuPos.x}px;top:${this._contextMenuPos.y}px">
        <button class="ctx-menu-item" @click=${() => this._downloadAsset(slug)}>
          <i class="bi bi-download"></i> Download Asset
        </button>
        <button class="ctx-menu-item" @click=${() => this._openUploadModal(slug)}>
          <i class="bi bi-upload"></i> Upload Alternate
        </button>
        <button class="ctx-menu-item" ?disabled=${!hasAlt} @click=${() => this._toggleUseAlternate(slug)}>
          <span class="ctx-menu-check">${isActive ? '\u2713' : ''}</span> Use Alternate
        </button>
        <button class="ctx-menu-item" ?disabled=${!hasAlt} @click=${() => this._removeAlternate(slug)}>
          <i class="bi bi-trash"></i> Remove Alternate
        </button>
      </div>
    `;
  }

  _renderUploadModal() {
    if (!this._uploadModalSlug) return nothing;
    return html`
      <div
        class="upload-backdrop"
        @click=${(/** @type {MouseEvent} */ e) => {
          if (e.target === e.currentTarget) this._closeUploadModal();
        }}
      >
        <div class="upload-dialog">
          <h6 class="mb-3">Upload Alternate: ${this._uploadModalSlug}</h6>
          <div
            class="drop-zone"
            @dragover=${this._onDropZoneDragOver}
            @dragleave=${this._onDropZoneDragLeave}
            @drop=${this._onDropZoneDrop}
            @click=${() =>
              /** @type {HTMLInputElement|null} */ (this.renderRoot?.querySelector('#alt-file-input'))?.click()}
          >
            ${
              this._uploadPreviewUrl
                ? html`<img src="${this._uploadPreviewUrl}" alt="Preview" />`
                : html`<div class="text-muted">Drop an image here or click to choose</div>`
            }
          </div>
          <input
            id="alt-file-input"
            type="file"
            accept="image/*"
            style="display:none"
            @change=${this._onFileInputChange}
          />
          <div class="d-flex justify-content-end gap-2 mt-3">
            <button class="btn btn-sm btn-outline-secondary" @click=${this._closeUploadModal}>Cancel</button>
            <button class="btn btn-sm btn-primary" ?disabled=${!this._uploadBlob} @click=${this._saveUploadedAlternate}>
              Save
            </button>
          </div>
        </div>
      </div>
    `;
  }
}

customElements.define('staff-paperdoll-view', StaffPaperdollView);
